import { inject, injectable } from "tsyringe";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { autoBind } from "src/shared/decorators/autobind";
import {
    Beneficiary,
    PolicyClient,
    Insured,
    PgaPolicy,
    PgaPolicyDetail,
} from "src/shared/models";
import { PolicyBridgeService } from "src/containers/insurances/services/policy-bridge/policy-bridge-service";
import { PolicyDetailRepository } from "src/containers/insurances/repositories/policy-detail/policy-detail-repository";

export type PolicyClientPayload = {
    policyId: number;
    userIdentity: string;
    systemCode: string;
    clientId: number | string;
    businessLine: string;
};

export type PolicyDetailPayload = {
    policyId: number;
    userIdentity: string;
    systemCode: string;
    businessLine: string;
};

@injectable()
export class PolicyDetailService {
    constructor(
        @inject("PolicyDetailRepository")
        private repository: PolicyDetailRepository,
        @inject("PolicyBridgeService")
        private bridgeService: PolicyBridgeService
    ) {}

    @autoBind
    getDetail(payload: PolicyDetailPayload): Observable<PgaPolicyDetail> {
        return this.repository
            .getDetail(payload)
            .pipe(
                map((data) => this.bridgeService.standarizePolicyDetail(data))
            );
    }

    @autoBind
    getClient(payload: PolicyClientPayload): Observable<PolicyClient> {
        return this.repository
            .getClient(payload)
            .pipe(map((data) => PolicyClient.fromJson(data)));
    }

    @autoBind
    getInsureds(payload: PolicyDetailPayload): Observable<Array<Insured>> {
        return this.repository
            .getInsureds(payload)
            .pipe(map((data) => data.map((i) => Insured.fromJson(i))));
    }

    @autoBind
    getBeneficiaries(
        userIdentity: string,
        policy: PgaPolicy
    ): Observable<Array<Beneficiary>> {
        return this.repository
            .getBeneficiaries(userIdentity, policy)
            .pipe(map((data) => data.map((b) => Beneficiary.fromJson(b))));
    }
}
